import React, { useMemo } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { TransactionApprovalTemplateModel } from 'typings/approvalManagement/accountManagement';

// components
import {
  Text,
  Divider,
  IconSVG,
  Table,
  RequiredPropsForTableModel,
  TableColumnModel,
} from '@ui';
import TableActions from 'components/Tables/TableActions';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import EllipsisTooltip from 'components/Tooltips/EllipsisTooltip';
import TransactionApprovalRulesMatrix from 'components/Tables/TransactionApprovalRulesMatrix';
import { ReactComponent as ErrorIcon } from 'resources/icons/remix-icons/close-circle-line.svg';

export type TableRecord = {
  id: string;
  bankAccounts: { name: string; purpose: string }[];
  approvalMatrix: TransactionApprovalTemplateModel;
  errors?: string[];
};

export type ActionKeys = 'edit';

interface IProps extends RequiredPropsForTableModel<TableRecord> {
  onActionsClick?: (key: ActionKeys, record: TableRecord) => void;
}

const OnboardingTransactionRulesTable = ({
  onActionsClick,
  ...rest
}: IProps) => {
  const { t } = useTranslation('onboarding');

  const tableColumns = useMemo<TableColumnModel[]>(
    () => [
      {
        key: 'account_number',
        title: t(
          'administration.transaction_rules.approval_rules.table.bank_accounts',
        ),
        width: 350,
        render: (record: TableRecord) => (
          <>
            {record.errors?.length ? (
              <DivAlignCenter>
                <StyledIconSVG
                  color={colorsTheme.colorError}
                  component={ErrorIcon}
                />
                <Text color={colorsTheme.colorError} gutterBottom>
                  {t(
                    'administration.transaction_rules.approval_rules.table.invalid_approval_rule',
                  )}
                </Text>
              </DivAlignCenter>
            ) : null}
            {record.bankAccounts.map((e, i) => (
              <React.Fragment key={i}>
                <Text
                  variant="body1"
                  weight="semi-bold"
                  color={colorsTheme.colorWhite}
                >
                  <EllipsisTooltip maxTextContainerWidth="300px" title={e.name}>
                    {e.name}
                  </EllipsisTooltip>
                </Text>
                <Text variant="body1">
                  <DivAlignCenter>
                    <Text weight="semi-bold">
                      {t(
                        'administration.transaction_rules.approval_rules.table.purpose_of_bank_account',
                      )}
                    </Text>
                    <StyledEllipsisTooltip
                      maxTextContainerWidth="200px"
                      title={e.purpose}
                    >
                      {e.purpose}
                    </StyledEllipsisTooltip>
                  </DivAlignCenter>
                </Text>
                {i < record.bankAccounts.length - 1 && <StyledDivider />}
              </React.Fragment>
            ))}
          </>
        ),
      },
      {
        key: 'transaction_approval_matrix',
        title: t(
          'administration.transaction_rules.approval_rules.table.approval_rule',
        ),
        render: (record: TableRecord) => (
          <TransactionApprovalRulesMatrix
            transactionRule={record.approvalMatrix}
          />
        ),
      },
      {
        hidden: !onActionsClick,
        align: 'right',
        key: 'actions',
        width: 70,
        render: (record: TableRecord) => (
          <TableActions
            onSelect={(key) =>
              onActionsClick && onActionsClick(key as ActionKeys, record)
            }
            menu={
              [
                {
                  key: 'edit',
                  name: t(
                    'administration.transaction_rules.approval_rules.table.edit_rule_btn',
                  ),
                },
              ] as {
                key: ActionKeys;
                name: string;
              }[]
            }
          />
        ),
      },
    ],
    [onActionsClick],
  );

  return <Table disableMobileMode columns={tableColumns} {...rest} />;
};

const StyledDivider = styled(Divider)`
  margin: ${({ theme }) => `${theme.paddingSm} 0px`};
`;

const StyledEllipsisTooltip = styled(EllipsisTooltip)`
  margin-left: ${({ theme }) => theme.marginXXs};
`;

const StyledIconSVG = styled(IconSVG)`
  margin-right: ${({ theme }) => theme.marginSm};
`;

export default OnboardingTransactionRulesTable;
